import { type FC } from 'react'

import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'

export type PricingLineItem = {
  label: string
  amount: string
}

export type RentCreditInfo = {
  fromLabel: string
  pricePerMonth: string
  periodIncludes: string
}

export type MarketplacePricingSummaryProps = {
  lineItems: PricingLineItem[]
  total: PricingLineItem
  vatNote?: string
  /** When provided, shows rent/credit section instead of total (buy price from lineItems if available) */
  rentCreditInfo?: RentCreditInfo
}

export const MarketplacePricingSummary: FC<MarketplacePricingSummaryProps> = ({
  lineItems,
  total,
  vatNote,
  rentCreditInfo,
}) => {
  const isBuyMode = !rentCreditInfo
  const hasBuySection = lineItems.length > 0

  return (
    <F0Box
      display="flex"
      flexDirection="column"
      gap="md"
      padding="lg"
      borderRadius="md"
      background="secondary"
    >
      {/* Line items (Device, add-ons) */}
      {lineItems.map((item, index) => (
        <F0Box
          key={index}
          display="flex"
          flexDirection="row"
          justifyContent="between"
          alignItems="center"
        >
          <F0Text variant="description" content={item.label} />
          <F0Text variant="body" content={item.amount} />
        </F0Box>
      ))}

      {/* Separator - only when we have content above */}
      {hasBuySection && <div className="h-px w-full border-t border-f1-border" />}

      {isBuyMode ? (
        <>
          {/* Total (buy mode) */}
          <F0Box display="flex" flexDirection="row" justifyContent="between" alignItems="center">
            <F0Text variant="label" content={total.label} />
            <F0Heading variant="heading" content={total.amount} />
          </F0Box>
        </>
      ) : (
        <>
          {/* Rent/credit section */}
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Box display="flex" flexDirection="row" justifyContent="between" alignItems="center">
              <F0Text variant="label" content={rentCreditInfo.fromLabel} />
              <F0Heading variant="heading" content={rentCreditInfo.pricePerMonth} />
            </F0Box>
            <F0Text variant="small" content={rentCreditInfo.periodIncludes} />
          </F0Box>
        </>
      )}

      {/* VAT note */}
      {vatNote && <F0Text variant="small" content={vatNote} />}
    </F0Box>
  )
}
